"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { SwiperSlide } from "swiper/react";
import Swiper from "@/components/swiper";
import smoothScrollToElement from "@/utils/smoothScroll";

const GalleryDetailPopup = ({
  isOpen,
  onClose,
  onCtaClick,
  images = [],
  initialIndex = 0,
  ctaLabel = "Rezervasyon Yap",
}) => {
  const validImages = useMemo(
    () => images.filter((image) => Boolean(image?.src)),
    [images],
  );
  const clampedInitialIndex = useMemo(
    () =>
      Math.min(Math.max(initialIndex, 0), Math.max(validImages.length - 1, 0)),
    [initialIndex, validImages.length],
  );
  const [activeIndex, setActiveIndex] = useState(clampedInitialIndex);
  const swiperRef = useRef(null);

  useEffect(() => {
    if (!isOpen || validImages.length === 0) {
      return undefined;
    }
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen, validImages.length]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose?.();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  const swiperOptions = useMemo(() => {
    const hasMultipleImages = validImages.length > 1;
    return {
      loop: hasMultipleImages,
      slidesPerView: 1,
      spaceBetween: 0,
      centeredSlides: false,
      allowTouchMove: hasMultipleImages,
      initialSlide: clampedInitialIndex,
    };
  }, [validImages.length, clampedInitialIndex]);

  const totalImages = validImages.length;
  const normalizedActiveIndex =
    totalImages > 0 ? Math.min(Math.max(activeIndex, 0), totalImages - 1) : 0;
  const paginationLabel = `${normalizedActiveIndex + 1}/${totalImages}`;

  const condensedDots = useMemo(() => {
    if (totalImages === 0) {
      return [];
    }
    const dotCount = Math.min(totalImages, 5);
    const activeDot = Math.min(
      Math.floor((normalizedActiveIndex / totalImages) * dotCount),
      dotCount - 1,
    );

    return Array.from({ length: dotCount }, (_, index) => index === activeDot);
  }, [normalizedActiveIndex, totalImages]);

  const goToPrev = () => {
    swiperRef.current?.prevSlide();
  };

  const goToNext = () => {
    swiperRef.current?.nextSlide();
  };

  if (typeof window === "undefined" || !isOpen || validImages.length === 0) {
    return null;
  }

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  const handleCtaClick = () => {
    // Eğer onCtaClick prop'u varsa onu kullan (parent popup'ı da kapatır)
    // Yoksa sadece bu popup'ı kapat
    if (onCtaClick) {
      onCtaClick();
    } else {
      onClose?.();

      // Popup kapandıktan sonra scroll yap (kısa bir gecikme ile)
      setTimeout(() => {
        // HotelRooms component'ine scroll yap
        const roomsElement = document.getElementById("rooms");
        if (roomsElement) {
          smoothScrollToElement(roomsElement, 108, 1800);
        }
      }, 100);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-[#101A44] px-0 py-6 min-[720px]:px-6 md:px-4"
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      <button
        type="button"
        aria-label="Detay galerisini kapat"
        onClick={() => onClose?.()}
        className="absolute top-12 right-15 z-20 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-white transition hover:text-white/80 focus:ring-2 focus:ring-white focus:outline-none"
      >
        <FontAwesomeIcon icon={byPrefixAndName.far["xmark"]} fontSize={28} />
      </button>
      <div className="relative mx-auto flex h-full w-full flex-col justify-between gap-6 rounded-none px-0 py-6 text-white min-[720px]:justify-center min-[720px]:gap-10 min-[720px]:rounded-3xl min-[720px]:py-0 md:px-6">
        <div className="mx-auto flex max-h-[683px] w-full flex-1 items-center justify-center gap-4 md:gap-10">
          {validImages.length > 1 ? (
            <button
              type="button"
              onClick={goToPrev}
              className="text-foreground-muted hidden h-12! w-12! shrink-0 cursor-pointer items-center justify-center rounded-full bg-white shadow-lg transition hover:scale-[1.02] focus:ring-2 focus:ring-white focus:outline-none min-[720px]:flex"
              aria-label="Önceki görsel"
            >
              <FontAwesomeIcon
                icon={byPrefixAndName.far["chevron-left"]}
                className="h-6! w-6!"
              />
            </button>
          ) : null}

          <div className="relative flex w-full justify-center overflow-hidden min-[720px]:h-[460px] min-[720px]:w-[900px] min-[1024px]:h-[440px] min-[1024px]:w-[880px] xl:h-[620px] xl:w-[1024px]">
            <Swiper
              key={`${initialIndex}-${validImages.length}`}
              ref={swiperRef}
              customClass="relative h-full w-full overflow-hidden"
              swiperOptions={swiperOptions}
              onSlideChange={(swiperInstance) => {
                setActiveIndex(swiperInstance.realIndex);
              }}
            >
              {validImages.map((image, index) => (
                <SwiperSlide
                  key={`${image.id}-${index}`}
                  className="flex justify-center"
                >
                  <div className="relative aspect-video h-full max-h-[75vh] w-full overflow-hidden bg-white/5 shadow-2xl min-[720px]:aspect-auto min-[720px]:rounded-lg">
                    <Image
                      src={image.src}
                      alt={image.alt ?? ""}
                      fill
                      priority
                      unoptimized
                      sizes="(min-width: 1280px) 60vw, (min-width: 768px) 70vw, 90vw"
                      className="object-cover"
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>

            {validImages.length > 1 ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-6 flex items-center justify-center min-[720px]:hidden">
                <div className="pointer-events-auto flex w-full max-w-[320px] items-center justify-between rounded-full bg-black/40 px-4 py-3 text-xs backdrop-blur">
                  <button
                    type="button"
                    aria-label="Önceki görsel"
                    onClick={goToPrev}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30 focus:ring-2 focus:ring-white focus:outline-none"
                  >
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["chevron-left"]}
                      fontSize={16}
                    />
                  </button>
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-1">
                      {condensedDots.map((isActive, dotIndex) => (
                        <span
                          key={`dot-${dotIndex}`}
                          className={`h-1.5 rounded-full transition-all duration-200 ${
                            isActive ? "w-4 bg-white" : "w-2 bg-white/50"
                          }`}
                        />
                      ))}
                    </div>
                    <span className="rounded-full bg-white/20 px-3 py-0.5 text-[11px] font-medium text-white">
                      {paginationLabel}
                    </span>
                  </div>
                  <button
                    type="button"
                    aria-label="Sonraki görsel"
                    onClick={goToNext}
                    className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white transition hover:bg-white/30 focus:ring-2 focus:ring-white focus:outline-none"
                  >
                    <FontAwesomeIcon
                      icon={byPrefixAndName.far["chevron-right"]}
                      fontSize={16}
                    />
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {validImages.length > 1 ? (
            <button
              type="button"
              onClick={goToNext}
              className="text-foreground-muted hidden h-12! w-12! shrink-0 cursor-pointer items-center justify-center rounded-full bg-white shadow-lg transition hover:scale-[1.02] focus:ring-2 focus:ring-white focus:outline-none min-[720px]:flex"
              aria-label="Sonraki görsel"
            >
              <FontAwesomeIcon
                icon={byPrefixAndName.far["chevron-right"]}
                className="h-6! w-6!"
              />
            </button>
          ) : null}
        </div>

        <div className="flex flex-col items-center gap-4 px-4 text-center min-[720px]:px-0">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleCtaClick();
            }}
            className="inline-flex w-full max-w-[360px] items-center justify-center rounded-xl border border-white px-6 py-3 text-base font-medium text-white transition hover:bg-white/10 focus:ring-2 focus:ring-white focus:outline-none min-[720px]:w-auto min-[720px]:max-w-none min-[720px]:px-12"
          >
            {ctaLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default GalleryDetailPopup;
